/**
 * Partner Hooks - React hooks for partner data management
 */

import {
  checkPartnerDuplicate,
  createPartner,
  deletePartner,
  getCostBearerClients,
  getPartnerById,
  getPartners,
  getPartnerStats,
  quickCreatePartner,
  setCostBearer,
  updatePartner,
  updatePartnerStatus,
  type Partner,
  type PartnerFilters,
  type PartnerListResponse,
  type PartnerStats,
} from '@/api/partners';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface UsePartnersResult {
  partners: Partner[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UsePartnerResult {
  partner: Partner | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UsePartnerStatsResult {
  stats: PartnerStats | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UsePartnerMutationsResult {
  createPartner: typeof createPartner;
  updatePartner: typeof updatePartner;
  deletePartner: typeof deletePartner;
  updatePartnerStatus: typeof updatePartnerStatus;
  setCostBearer: typeof setCostBearer;
  isLoading: boolean;
  error: string | null;
}

// ============================================
// HOOKS
// ============================================

/**
 * Hook to fetch and manage partner list
 */
export function usePartners(filters: PartnerFilters = {}): UsePartnersResult {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 20, hasMore: false });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPartners = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: PartnerListResponse = await getPartners(filters);
      setPartners(response.data);
      setMeta(response.meta);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch partners';
      setError(message);
      console.error('Error fetching partners:', err);
    } finally {
      setIsLoading(false);
    }
  }, [
    filters.type,
    filters.status,
    filters.search,
    filters.page,
    filters.pageSize,
    filters.hasRentals,
  ]);

  useEffect(() => {
    void fetchPartners();
  }, [fetchPartners]);

  return {
    partners,
    total: meta.total,
    page: meta.page,
    pageSize: meta.pageSize,
    hasMore: meta.hasMore,
    isLoading,
    error,
    refetch: fetchPartners,
  };
}

/**
 * Hook to fetch single partner
 */
export function usePartner(id: string | undefined): UsePartnerResult {
  const [partner, setPartner] = useState<Partner | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPartner = useCallback(async () => {
    if (!id) {
      setPartner(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getPartnerById(id);
      setPartner(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch partner';
      setError(message);
      console.error('Error fetching partner:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchPartner();
  }, [fetchPartner]);

  return {
    partner,
    isLoading,
    error,
    refetch: fetchPartner,
  };
}

/**
 * Hook to fetch partner statistics
 */
export function usePartnerStats(): UsePartnerStatsResult {
  const [stats, setStats] = useState<PartnerStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getPartnerStats();
      setStats(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch partner stats';
      setError(message);
      console.error('Error fetching partner stats:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  return {
    stats,
    isLoading,
    error,
    refetch: fetchStats,
  };
}

/**
 * Hook for partner mutations (create, update, delete)
 */
export function usePartnerMutations(): UsePartnerMutationsResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // M-023 fix: Wrap all mutation functions in useCallback for stable references
  const wrappedCreate: typeof createPartner = useCallback(async data => {
    setIsLoading(true);
    setError(null);
    try {
      return await createPartner(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create partner';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const wrappedUpdate: typeof updatePartner = useCallback(async (id, data) => {
    setIsLoading(true);
    setError(null);
    try {
      return await updatePartner(id, data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update partner';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const wrappedDelete: typeof deletePartner = useCallback(async id => {
    setIsLoading(true);
    setError(null);
    try {
      return await deletePartner(id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete partner';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const wrappedUpdateStatus: typeof updatePartnerStatus = useCallback(async (id, data) => {
    setIsLoading(true);
    setError(null);
    try {
      return await updatePartnerStatus(id, data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Státusz frissítés sikertelen';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const wrappedSetCostBearer: typeof setCostBearer = useCallback(async (id, costBearerId) => {
    setIsLoading(true);
    setError(null);
    try {
      return await setCostBearer(id, costBearerId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Költségviselő beállítás sikertelen';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    createPartner: wrappedCreate,
    updatePartner: wrappedUpdate,
    deletePartner: wrappedDelete,
    updatePartnerStatus: wrappedUpdateStatus,
    setCostBearer: wrappedSetCostBearer,
    isLoading,
    error,
  };
}

/**
 * Hook to fetch cost bearer clients (E-55 Story 55-3)
 */
export function useCostBearerClients(costBearerId: string | undefined) {
  const [clients, setClients] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClients = useCallback(async () => {
    if (!costBearerId) {
      setClients([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getCostBearerClients(costBearerId);
      setClients(response.data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Költségviselő ügyfelek betöltése sikertelen';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [costBearerId]);

  useEffect(() => {
    void fetchClients();
  }, [fetchClients]);

  return { clients, isLoading, error, refetch: fetchClients };
}

/**
 * Hook for quick creating a partner (ADR-089)
 */
export function useQuickCreatePartner() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (data: {
      type: 'INDIVIDUAL' | 'COMPANY';
      name: string;
      taxNumber?: string;
      email?: string;
      phone?: string;
      city?: string;
      /** Story 70-1E-a: OCR-prefilled (forwarded to quickCreatePartner). */
      idCardNumber?: string;
      idCardExpiry?: string;
    }) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await quickCreatePartner(data);
        return result.data;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Partner létrehozás sikertelen';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { create, isLoading, error };
}

/**
 * Hook for checking partner duplicates (ADR-089)
 */
export function useCheckPartnerDuplicate() {
  const [duplicates, setDuplicates] = useState<
    Array<{ id: string; name: string; taxNumber?: string; type: string }>
  >([]);

  const check = useCallback(async (params: { name?: string; taxNumber?: string }) => {
    if (
      (!params.name || params.name.length < 2) &&
      (!params.taxNumber || params.taxNumber.length < 4)
    ) {
      setDuplicates([]);
      return;
    }
    try {
      const result = await checkPartnerDuplicate(params);
      setDuplicates(result.data);
    } catch {
      setDuplicates([]);
    }
  }, []);

  return { duplicates, check, clear: useCallback(() => setDuplicates([]), []) };
}
